import type { GuideCheck, GuideCollection, GuidePage } from "../types";

// Pure helpers for Omni Hub ▸ Guides (pages/guides/GuidesPage.tsx): the route
// shape, the page menu, and the links a guide page carries to its siblings.

/** /<org>/guides[/<collection>[/<slug>]] → the open collection and page. */
export function guidePathParts(pathname: string): { collection: string | null; slug: string | null } {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[1] !== "guides") return { collection: null, slug: null };
  return { collection: parts[2] ? decodeURIComponent(parts[2]) : null, slug: parts[3] ? decodeURIComponent(parts[3]) : null };
}

export function guideHref(org: string, collection: string, slug?: string | null, anchor?: string | null): string {
  // Every segment encoded, the org included: it comes from the org <select>, and
  // these URLs are written into rendered links.
  const base = `/${encodeURIComponent(org)}/guides/${encodeURIComponent(collection)}`;
  const withPage = slug ? `${base}/${encodeURIComponent(slug)}` : base;
  return anchor ? `${withPage}#${anchor}` : withPage;
}

/** The file a page's relative media reference names — `assets/<file>` (or
 *  `./assets/<file>`) — or null for anything else (absolute URLs, other paths). */
export function guideAssetName(src: string): string | null {
  return src.match(/^(?:\.\/)?assets\/([A-Za-z0-9][A-Za-z0-9._-]*)$/)?.[1] ?? null;
}

/** A check's section number — "§3.2 Module status" → "3.2", "§1 Overview" → "1". */
export function sectionNumber(section: string): string | null {
  return section.match(/§\s*(\d+(?:\.\d+)*)/)?.[1] ?? null;
}

/** Whether a heading's text is that section's: "3.2 Module status", "1. Module
 *  overview" — but "1.1 …" is not section 1, and "3.21 …" is not 3.2. */
export function headingIsSection(text: string, num: string): boolean {
  // A plain prefix comparison: the heading starts with the number, then an
  // optional dot, then whitespace. (No RegExp built from the number.)
  const t = text.trim();
  if (!num || !t.startsWith(num)) return false;
  const rest = t.slice(num.length);
  return /^\.?\s/.test(rest);
}

/** Checks in section order, grouped by section (consecutive runs). */
export function groupChecks(checks: readonly GuideCheck[]): { section: string; checks: GuideCheck[] }[] {
  const out: { section: string; checks: GuideCheck[] }[] = [];
  for (const c of checks) {
    const last = out[out.length - 1];
    if (last && last.section === c.section) last.checks.push(c);
    else out.push({ section: c.section, checks: [c] });
  }
  return out;
}

/** The page a collection opens on: its landing page, else its first page. */
export function defaultGuidePage(collection: GuideCollection): GuidePage | null {
  return collection.pages.find((p) => p.landing) ?? collection.pages[0] ?? null;
}

export interface GuideMenuGroup {
  /** null for the collection's top-level pages. */
  title: string | null;
  pages: GuidePage[];
}

/** Consecutive pages sharing a group form one menu block, in collection order. */
export function guideMenu(collection: GuideCollection): GuideMenuGroup[] {
  const out: GuideMenuGroup[] = [];
  for (const page of collection.pages) {
    const last = out[out.length - 1];
    if (last && last.title === page.group) last.pages.push(page);
    else out.push({ title: page.group, pages: [page] });
  }
  return out;
}

/**
 * Every link a guide page can make to another guide page, mapped to its in-app
 * URL. Guides link to each other the way corpus pages do — `/<collection>/<slug>/`
 * — so a page reads the same if it later moves into a docs version. The table is
 * built from the collection list alone: a link's text is only ever a KEY into it,
 * never part of the URL written back into the page.
 */
export function guideLinkTargets(org: string, collections: readonly GuideCollection[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const c of collections) {
    for (const p of c.pages) {
      const url = guideHref(org, c.id, p.slug);
      out.set(`/${c.id}/${p.slug}/`, url);
      out.set(`/${c.id}/${p.slug}`, url);
    }
  }
  return out;
}
